import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

function RecordingStatus({ book, chapter, startVerse, endVerse }) {
  const [existingRecordings, setExistingRecordings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchExistingRecordings = async () => {
      if (!book || !chapter) return;
      
      setLoading(true);
      try {
        const q = query(
          collection(db, 'recordings'),
          where('book', '==', book.toLowerCase()),
          where('chapter', '==', chapter)
        );
        
        const querySnapshot = await getDocs(q);
        const recordings = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setExistingRecordings(recordings);
      } catch (error) {
        console.error('Error fetching existing recordings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchExistingRecordings();
  }, [book, chapter]);

  if (loading) {
    return (
      <div className="card mb-4">
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
          <span className="ml-2 text-gray-600">Checking existing recordings...</span>
        </div>
      </div>
    );
  }

  // Get all verses in the selected range
  const selectedVerses = [];
  for (let v = startVerse; v <= endVerse; v++) {
    selectedVerses.push(v);
  }

  // Find which verses already have recordings
  const recordedVerses = new Set();
  const missingVerses = [];
  
  existingRecordings.forEach(recording => {
    // Handle both old format (startVerse/endVerse) and new format (verse)
    if (recording.verse) {
      // New format: single verse
      if (selectedVerses.includes(recording.verse)) {
        recordedVerses.add(recording.verse);
      }
    } else if (recording.startVerse) {
      // Old format: verse range
      for (let v = recording.startVerse; v <= recording.endVerse; v++) {
        if (selectedVerses.includes(v)) {
          recordedVerses.add(v);
        }
      }
    }
  });

  // Find missing verses
  selectedVerses.forEach(verse => {
    if (!recordedVerses.has(verse)) {
      missingVerses.push(verse);
    }
  });

  // Group recordings by verse for display
  const recordingsByVerse = {};
  existingRecordings.forEach(recording => {
    // Handle both old format (startVerse/endVerse) and new format (verse)
    if (recording.verse) {
      // New format: single verse
      if (selectedVerses.includes(recording.verse)) {
        if (!recordingsByVerse[recording.verse]) {
          recordingsByVerse[recording.verse] = [];
        }
        recordingsByVerse[recording.verse].push(recording);
      }
    } else if (recording.startVerse) {
      // Old format: verse range
      for (let v = recording.startVerse; v <= recording.endVerse; v++) {
        if (selectedVerses.includes(v)) {
          if (!recordingsByVerse[v]) {
            recordingsByVerse[v] = [];
          }
          recordingsByVerse[v].push(recording);
        }
      }
    }
  });

  if (existingRecordings.length === 0) {
    return (
      <div className="card mb-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">No existing recordings</h3>
              <p className="text-sm text-blue-700 mt-1">
                This passage has no recordings yet. You'll be the first to record it!
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card mb-4">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Recording Status</h3>
      
      {/* Summary */}
      <div className="bg-gray-50 rounded-lg p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-gray-900">{selectedVerses.length}</div>
            <div className="text-sm text-gray-600">Total Verses</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-600">{recordedVerses.size}</div>
            <div className="text-sm text-gray-600">Already Recorded</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-orange-600">{missingVerses.length}</div>
            <div className="text-sm text-gray-600">Still Need Recording</div>
          </div>
        </div>
      </div>

      {/* Existing Recordings */}
      {Object.keys(recordingsByVerse).length > 0 && (
        <div className="mb-4">
          <h4 className="font-medium text-gray-900 mb-2">Existing Recordings:</h4>
          <div className="space-y-2">
            {Object.entries(recordingsByVerse).map(([verse, recordings]) => (
              <div key={verse} className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="font-medium text-green-800">Verse {verse}</span>
                    <span className="text-sm text-green-600">({recordings.length} recording{recordings.length > 1 ? 's' : ''})</span>
                  </div>
                  <div className="text-sm text-green-600">
                    {recordings.map(rec => rec.name || rec.userEmail).join(', ')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Missing Verses */}
      {missingVerses.length > 0 && (
        <div className="mb-4">
          <h4 className="font-medium text-gray-900 mb-2">Still Need Recording:</h4>
          <div className="space-y-2">
            {missingVerses.map(verse => (
              <div key={verse} className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                    <span className="font-medium text-orange-800">Verse {verse}</span>
                  </div>
                  <span className="text-sm text-orange-600">No recordings yet</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Suggestions */}
      {missingVerses.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">Recording Suggestions</h3>
              <div className="text-sm text-blue-700 mt-1">
                {missingVerses.length === 1 ? (
                  <p>You only need to record verse {missingVerses[0]}. Consider recording just that verse instead of the entire range.</p>
                ) : (
                  <p>Consider recording just the missing verses ({missingVerses.join(', ')}) instead of the entire range to avoid duplication.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RecordingStatus;
